import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import api from '../../services/api';
import './BannersTable.css';
import ViewBannerModal from './ViewBannerModal';
import EditBannerModal from './EditBannerModal';
import AddBannerModal from './AddBannerLayer';

const BannersTable = () => {
    const [banners, setBanners] = useState([]);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [currentBanner, setCurrentBanner] = useState(null);
    const [editedBanner, setEditedBanner] = useState(null);

    const fetchBanners = async () => {
        try {
            const res = await api.get('/banners');
            if (res.data.response) {
                setBanners(res.data.response.data);
            }
        } catch (error) {
            console.error('Error fetching banners:', error);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const handleView = (banner) => {
        setCurrentBanner(banner);
        setShowViewModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setCurrentBanner(null);
        fetchBanners();
    };

    const handleEdit = (banner) => {
        setEditedBanner(banner);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditedBanner(null);
        fetchBanners();
    };

    const handleSaveEdit = (updatedBanner) => {
        const updated = banners.map((b) => (b.id === updatedBanner.id ? updatedBanner : b));
        setBanners(updated);
        setShowEditModal(false);
        setEditedBanner(null);
        fetchBanners();
    };

    const handleDelete = async (banner) => {
        try {
            await api.put(`/banners/${banner.id}`, { isDeleted: 1 });
            toast.success('Banner deleted successfully');
            fetchBanners();
        } catch (error) {
            console.error('Error deleting banner:', error);
            toast.error('Failed to delete banner');
        }
    };

    const handleToggleShown = async (banner) => {
        try {
            await api.put(`/banners/${banner.id}`, { isShown: banner.isShown === 1 ? 0 : 1 });
            fetchBanners();
        } catch (error) {
            console.error('Error toggling isShown:', error);
            toast.error('Failed to toggle isShown');
        }
    };

    const handleAddBanner = () => {
        setShowAddModal(true);
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
    };

    const columns = [
        {
            name: 'Project ID',
            selector: (row) => row.project_id,
            sortable: true,
            width: '100px',
        },
        {
            name: 'No.',
            selector: (row) => row.id,
            sortable: true,
            width: '70px',
        },
        {
            name: 'Nickname',
            selector: (row) => row.nickname,
            sortable: true,
        },
        {
            name: 'Image',
            cell: (row) => (
                <img
                    src={row.image_path}
                    alt={row.nickname}
                    style={{ maxWidth: '100px', maxHeight: '100px' }}
                />
            ),
        },
        {
            name: 'Is Shown',
            cell: (row) => (
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={row.isShown === 1}
                        onChange={() => handleToggleShown(row)}
                    />
                    <span className="slider round"></span>
                </label>
            ),
        },
        {
            name: 'Actions',
            cell: (row) => (
                <div className="d-flex flex-wrap align-items-center gap-2">
                    <button
                        key="view"
                        className="btn btn-primary btn-sm"
                        onClick={() => handleView(row)}
                    >
                        View
                    </button>
                    <button
                        key="edit"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleEdit(row)}
                    >
                        Edit
                    </button>
                    <button
                        key="delete"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDelete(row)}
                    >
                        Delete
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const customStyles = {
        rows: {
            style: {
                fontSize: '15px',
            },
        },
    };

    return (
        <div className="card basic-data-table px-3 py-3">
            <div className="card-body">
                <button className="btn btn-success mb-3" onClick={handleAddBanner}>
                    Add Banner
                </button>
                <DataTable
                    columns={columns}
                    data={banners}
                    pagination
                    highlightOnHover
                    responsive
                    striped
                    customStyles={customStyles}
                    noDataComponent="No results"
                />
            </div>

            {showViewModal && (
                <ViewBannerModal
                    show={showViewModal}
                    handleClose={handleCloseViewModal}
                    banner={currentBanner}
                />
            )}

            {showEditModal && (
                <EditBannerModal
                    show={showEditModal}
                    handleClose={handleCloseEditModal}
                    banner={editedBanner}
                    handleSave={handleSaveEdit}
                />
            )}

            {showAddModal && (
                <AddBannerModal
                    show={showAddModal}
                    handleClose={handleCloseAddModal}
                    refreshTable={fetchBanners}
                />
            )}
        </div>
    );
};

export default BannersTable;